import { usePreviewStore } from "@/stores/preview-store";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Ссылка «перейти к строке N» для работы, материала или validation issue (US4).
 *
 * В исходнике акта работа может занимать несколько строк: основная (`excelRow`)
 * плюс строки-продолжения (`continuationRows`). Компонент рендерит кликабельный
 * label с tooltip, перечисляющим все связанные строки.
 *
 * Приоритет обработчиков клика (фича 028):
 * 1. `onClick` — явный кастомный handler, имеет максимальный приоритет.
 * 2. `fileId` — если передан, открывает Excel-preview в отдельной вкладке через
 *    `usePreviewStore.openOrFocus` (новое поведение по умолчанию для карточек
 *    расхождений сверки).
 * 3. Fallback — копирует Excel-диапазон вида `A{row}:N{row}` (или `A{min}:N{max}`
 *    при `continuationRows`) в clipboard и показывает toast. Используется в
 *    `upload-progress.tsx`, где fileId недоступен.
 */
export interface GoToRowLinkProps {
	/** Номер строки в исходнике Excel (1-based), FR-026. */
	excelRow: number;
	/** Доп. строки продолжения работы (continuation/hybrid), если есть. */
	continuationRows?: number[];
	/** Произвольная короткая подпись (по умолчанию «Строка N»). */
	label?: string;
	/** Handler для будущего Excel-вьювера. Имеет приоритет над fileId-based preview. */
	onClick?: (excelRow: number) => void;
	/**
	 * ID исходного файла. Если передан (и `onClick` не задан) — клик открывает
	 * `/preview.html?fileId=...&row=...` через `usePreviewStore.openOrFocus`.
	 */
	fileId?: number;
	/**
	 * Имя листа в книге (опционально). Если задано — preview активирует именно
	 * этот лист после загрузки.
	 */
	sheet?: string | null;
	className?: string;
}

export function GoToRowLink({
	excelRow,
	continuationRows,
	label,
	onClick,
	fileId,
	sheet,
	className,
}: GoToRowLinkProps) {
	const displayLabel = label ?? `Строка ${excelRow}`;
	const hasContinuations = continuationRows !== undefined && continuationRows.length > 0;
	const tooltip = hasContinuations
		? `Основная строка: ${excelRow}\n+ продолжения: ${continuationRows?.join(", ")}`
		: `Строка ${excelRow} в исходнике Excel`;

	const handleClick = async () => {
		if (onClick) {
			onClick(excelRow);
			return;
		}
		if (fileId !== undefined) {
			// Не подписываемся на изменения стора — getState() достаточно для one-shot вызова.
			usePreviewStore.getState().openOrFocus({ fileId, row: excelRow, sheet });
			return;
		}
		const range = hasContinuations
			? `A${Math.min(excelRow, ...(continuationRows ?? []))}:N${Math.max(excelRow, ...(continuationRows ?? []))}`
			: `A${excelRow}:N${excelRow}`;
		try {
			await navigator.clipboard.writeText(range);
			toast.success(`Скопировано: ${range}`);
		} catch {
			toast.error("Не удалось скопировать");
		}
	};

	return (
		<button
			type="button"
			title={tooltip}
			onClick={handleClick}
			className={
				className ??
				"inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono text-slate-700 rounded hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-slate-400"
			}
		>
			<ExternalLink className="h-3 w-3" aria-hidden="true" />
			{displayLabel}
			{hasContinuations && <span className="text-slate-500">+{continuationRows?.length}</span>}
		</button>
	);
}
